import axios from 'axios';

// Decode a JWT and return true if it's past its `exp` (expired). Tolerant —
// any malformed token is treated as NOT expired (the server will 401 it).
function isExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
}

// Send the user back to the login screen once. Skips public pages (the customer
// chat widget) and the login page itself so we never loop.
let redirecting = false;
function forceLogin() {
  if (redirecting) return;
  const path = window.location.pathname;
  if (path === '/login' || path.startsWith('/chat/')) return;
  redirecting = true;
  window.location.replace('/login');
}

// Attach the workspace JWT to every API request so the backend can authenticate
// and enforce page-level permissions. Public pages (e.g. the customer chat
// widget) simply have no token in localStorage, so nothing is attached.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && isExpired(token)) {
    // Stale/expired token: DROP it so it can never block future requests
    // (including the login POST itself), then bounce to /login only if we're on
    // a protected page. Let this request proceed WITHOUT the dead token.
    localStorage.removeItem('token');
    localStorage.removeItem('preToken');
    forceLogin();
    return config;
  }
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If the server rejects an AUTHENTICATED request as unauthenticated
// (expired/invalid token), send the user to the login page instead of leaving
// them on a blank screen. Never triggers on the login call itself.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || '';
    const isAuthCall = /\/api\/(users\/login|authorized-admin\/login)/.test(url);
    const hadToken = !!err.config?.headers?.Authorization;
    if (status === 401 && !isAuthCall && hadToken) {
      localStorage.removeItem('token');
      localStorage.removeItem('preToken');
      forceLogin();
    }
    // Read-only (test/demo) account tried to write: show a clear, non-fatal
    // notice instead of a silent failure. Backend blocks the write regardless.
    const code = err.response?.data?.code;
    if (status === 403 && (code === 'READ_ONLY' || code === 'ORDERS_BLOCKED')) {
      showReadOnlyToast(err.response?.data?.message || 'Not authorized for test accounts');
    }
    return Promise.reject(err);
  }
);

// Lightweight, dependency-free toast for read-only blocks. Debounced so a burst
// of blocked writes shows a single message.
let toastTimer = null;
function showReadOnlyToast(message) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById('ro-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'ro-toast';
    el.style.cssText = 'position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:99999;background:#0f172a;color:#fff;font:600 13px/1.4 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;padding:12px 18px;border-radius:12px;box-shadow:0 14px 40px rgba(15,23,42,.35);display:flex;align-items:center;gap:9px;max-width:90vw;opacity:0;transition:opacity .18s ease,transform .18s ease;';
    document.body.appendChild(el);
  }
  el.innerHTML = '<span style="font-size:15px">🔒</span><span>' + String(message).replace(/</g, '&lt;') + '</span>';
  requestAnimationFrame(() => { el.style.opacity = '1'; el.style.transform = 'translateX(-50%) translateY(0)'; });
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { if (el) el.style.opacity = '0'; }, 2600);
}
